import {AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component} from "@angular/core";
import {DetectMethodChanges, isVoid} from "../../utils";
import {ToolbarVisibilityManager} from "./toolbar-visibility-manager";
import {ToolbarElementDescriptor} from "./toolbar-visibility-catalog";

interface StagedRow {
    key: string;
    label: string;
    visible: boolean;
}

interface StagedColumn {
    title: string;
    rows: StagedRow[];
}

@Component({
    selector: 'ets-toolbar-visibility-dialog',
    templateUrl: 'toolbar-visibility-dialog.component.html',
    styleUrls: ['toolbar-visibility-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToolbarVisibilityDialogComponent implements AfterViewInit {

    constructor(private readonly _changeDetector: ChangeDetectorRef) {
    }

    private _resolve: (applied: boolean) => void;
    private _manager: ToolbarVisibilityManager;

    visible = false;
    columns: StagedColumn[] = [];

    ngAfterViewInit(): void {
        this._changeDetector.detach();
    }

    @DetectMethodChanges()
    show(manager: ToolbarVisibilityManager, descriptors: ReadonlyArray<ToolbarElementDescriptor>): Promise<boolean> {

        if (this.visible) {
            this.visible = false;
            this._changeDetector.detectChanges();
        }

        this._manager = manager;

        const topRows: StagedRow[] = [];
        const bottomRows: StagedRow[] = [];
        descriptors.forEach(d => {
            const row: StagedRow = {
                key: d.key,
                label: d.label,
                visible: manager.isVisible(d.key),
            };
            (d.row === 'top' ? topRows : bottomRows).push(row);
        });

        this.columns = [
            {title: 'Top Row', rows: topRows},
            {title: 'Bottom Row', rows: bottomRows},
        ];

        this.visible = true;

        return new Promise<boolean>(res => {
            this._resolve = res;
        });
    }

    @DetectMethodChanges()
    onApplyClick(): void {
        const staged = new Map<string, boolean>();
        this.columns.forEach(col => col.rows.forEach(r => staged.set(r.key, r.visible)));
        this._manager.applyBatch(staged);
        this._close(true);
    }

    @DetectMethodChanges()
    onCancelClick(): void {
        this._close(false);
    }

    @DetectMethodChanges()
    onHiding(): void {
        if (this.visible) {
            this._close(false);
        }
    }

    private _close(applied: boolean): void {
        this.visible = false;
        const resolve = this._resolve;
        this._resolve = null;
        this._manager = null;
        if (!isVoid(resolve)) {
            resolve(applied);
        }
    }
}
